import React, {useState} from 'react';

import {Button, CreateIcon, DeleteIcon, SmallLoader} from '@kit';

import {getApolloError} from '../../components/utils';
import {useDeleteConfirm} from '../../components/use-delete-confirm';
import {
	CharacterSheetEntryFragment,
	useCharacterSheetsQuery,
	useClassesQuery,
	useDeleteCharacterSheetMutation,
	useOriginsQuery,
	useSpeciesQuery,
} from '../../generated/types';
import {Link} from '../../lib/routes';
import type {CharacterState} from './character/model/character-state';
import {CreationMaster} from './character/master/creation-master';

import css from './sheets.module.css';

const dateFormat = new Intl.DateTimeFormat('ru-RU', {day: 'numeric', month: 'long', year: 'numeric'});

export const SheetList: React.FC = () => {
	const {data, loading, error, refetch} = useCharacterSheetsQuery({ssr: false, fetchPolicy: 'cache-and-network'});
	const [deleteSheet] = useDeleteCharacterSheetMutation();
	const [isCreating, setCreating] = useState(false);
	const {openConfirm, DeleteConfirm} = useDeleteConfirm({
		title: 'Удалить бланк персонажа?',
		callback: async (sheetId: string) => {
			await deleteSheet({variables: {id: sheetId}});
			await refetch();
		},
	});

	const closeCreation = () => {
		setCreating(false);
		refetch();
	};

	const sheets = data?.characterSheets || [];
	const titles = useTitleLookups();

	return (
		<div className={css.page}>
			<header className={css.header}>
				<h1>Бланки персонажей</h1>
				<Button appearance="primary" icon={<CreateIcon />} onClick={() => setCreating(true)}>
					Создать персонажа
				</Button>
			</header>

			{error && <div className={css.message}>{getApolloError(error).message}</div>}
			{loading && !data && <SmallLoader />}
			{data && !sheets.length && <p className={css.muted}>У вас пока нет персонажей.</p>}

			<ul className={css.sheetList}>
				{sheets.map((sheet) => (
					<li key={sheet.id} className={css.sheetCard}>
						<Link route="sheet" params={{id: sheet.id}} className={css.sheetLink}>
							<span className={css.sheetName}>{sheet.name || 'Безымянный персонаж'}</span>
							<SheetSummary sheet={sheet} titles={titles} />
						</Link>
						<Button
							appearance="subtle"
							icon={<DeleteIcon />}
							aria-label="Удалить"
							title="Удалить"
							onClick={() => openConfirm(sheet.id)}
						/>
					</li>
				))}
			</ul>

			<CreationMaster open={isCreating} onClose={closeCreation} />
			{DeleteConfirm}
		</div>
	);
};

type Titled = {id: string; title: string};

export type TitleLookups = {
	classes: Titled[];
	species: Titled[];
	origins: Titled[];
};

export const useTitleLookups = (): TitleLookups => ({
	classes: useClassesQuery({ssr: false}).data?.classes || [],
	species: useSpeciesQuery({ssr: false}).data?.species || [],
	origins: useOriginsQuery({ssr: false}).data?.origins || [],
});

export const SheetSummary: React.FC<{sheet: CharacterSheetEntryFragment; titles: TitleLookups}> = ({sheet, titles}) => {
	const {classes, species, origins} = titles;
	const state = (sheet.data || {}) as Partial<CharacterState>;

	const classesText = (state.classes || [])
		.map(({classId, level}) => `${classes.find((entry) => entry.id === classId)?.title || classId} ${level}`)
		.join(' / ');
	const details = [
		classesText,
		species.find((entry) => entry.id === state.specieId)?.title,
		origins.find((entry) => entry.id === state.originId)?.title,
	].filter(Boolean);

	return (
		<span className={css.sheetCaption}>
			{details.join(' · ') || 'Персонаж ещё не собран'}
			<span className={css.muted}> · изменён {dateFormat.format(new Date(sheet.updatedAt))}</span>
		</span>
	);
};
